import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import './NewProductWizard.css';
import { formatAppDate } from '../../utils/dateUtils';
import { fetchAllCustomers } from '../../utils/fetchAllPages';

// Current cost of an inventory item, same rule as the BOM editor (EditableBOMItems): average cost, else cost per unit.
// Used for the wizard's "Estimated cost" and sent as the BOM line's unit_cost (#160).
const itemUnitCost = (item) => parseFloat(item?.average_cost) || parseFloat(item?.cost_per_unit) || 0;

const WIZARD_DRAFT_KEY = 'wizard_product_draft';

const STEPS = ['workflow', 'productType', 'product', 'bom', 'done'];

const UOM_CODES = ['PCS', 'ROLL', 'SHEET', 'SQM', 'MTR', 'KG', 'BOX', 'LTR'];

const DEFAULT_WIZARD_DATA = {
  workflowId: null,
  workflowName: '',
  workflowIsNew: false,
  productTypeId: null,
  productTypeName: '',
  productTypeIsNew: false,
  productId: null,
  productName: '',
  productCode: '',
  bomId: null,
  bomName: '',
  bomSkipped: false,
};

// ── Inline autocomplete for inventory items ──────────────────────────────────
function ItemAutocomplete({ value, onSelect, allItems }) {
  const { t } = useTranslation('products');
  const [query, setQuery] = useState(value?.item_name ? `${value.item_code} — ${value.item_name}` : '');
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const wrapperRef = useRef(null);

  const filtered = query.length >= 1
    ? allItems.filter(i =>
        i.item_name.toLowerCase().includes(query.toLowerCase()) ||
        i.item_code.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 30)
    : [];

  // Recalculate position whenever the dropdown opens so it stays anchored
  // to the input even inside a scrollable modal (position:fixed escapes overflow clipping).
  useEffect(() => {
    if (open && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom, left: rect.left, width: rect.width });
    }
  }, [open]);

  const handleSelect = (item) => {
    setQuery(`${item.item_code} — ${item.item_name}`);
    setOpen(false);
    setHighlightIdx(0);
    onSelect(item);
  };

  const handleKeyDown = (e) => {
    if (!open || filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSelect(filtered[highlightIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="wizard-autocomplete-wrapper" ref={wrapperRef}>
      <input
        type="text"
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); setHighlightIdx(0); onSelect(null); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={handleKeyDown}
        placeholder={t('wizard.autocomplete.placeholder')}
      />
      {value && <span className="wizard-item-selected" title={t('wizard.autocomplete.selected')}>✓</span>}
      {open && filtered.length > 0 && (
        <div
          className="wizard-autocomplete-dropdown"
          style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
        >
          {filtered.map((item, idx) => (
            <div
              key={item.item_id}
              className={`wizard-autocomplete-option${idx === highlightIdx ? ' highlighted' : ''}`}
              onMouseDown={() => handleSelect(item)}
            >
              <strong>{item.item_code}</strong> — {item.item_name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Step 0: Workflow ─────────────────────────────────────────────────────────
function StepWorkflow({ data, onChange }) {
  const { t } = useTranslation('products');
  const [mode, setMode] = useState(data.workflowId && !data.workflowIsNew ? 'existing' : 'new');
  const [workflows, setWorkflows] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [tasks, setTasks] = useState(data.tasks || [{ task_name: '', task_type: '', estimated_duration_minutes: 60 }]);

  useEffect(() => {
    apiFetch(getApiUrl('/api/workflows/')).then(r => r.json()).then(d => {
      const list = Array.isArray(d) ? d : (d.workflows || []);
      setWorkflows(list.filter(w => w.is_active));
    }).catch(() => {});
    apiFetch(getApiUrl('/api/workflows/task-types')).then(r => r.json()).then(d => {
      setTaskTypes(Array.isArray(d) ? d : []);
    }).catch(() => {});
  }, []);

  const updateField = (field, value) => onChange({ ...data, [field]: value });

  const updateTask = (idx, field, value) => {
    const next = tasks.map((task, i) => i === idx ? { ...task, [field]: value } : task);
    setTasks(next);
    onChange({ ...data, tasks: next });
  };

  const addTask = () => {
    const next = [...tasks, { task_name: '', task_type: taskTypes[0]?.type || '', estimated_duration_minutes: 60 }];
    setTasks(next);
    onChange({ ...data, tasks: next });
  };

  const removeTask = (idx) => {
    const next = tasks.filter((_, i) => i !== idx);
    setTasks(next);
    onChange({ ...data, tasks: next });
  };

  const switchMode = (m) => {
    setMode(m);
    if (m === 'existing') {
      onChange({ ...data, workflowIsNew: false, workflow_name: '', tasks: [] });
    } else {
      onChange({ ...data, workflowIsNew: true, workflowId: null, workflowName: '' });
    }
  };

  return (
    <div>
      <div className="wizard-toggle">
        <button className={mode === 'existing' ? 'active' : ''} onClick={() => switchMode('existing')}>
          {t('wizard.workflow.useExisting')}
        </button>
        <button className={mode === 'new' ? 'active' : ''} onClick={() => switchMode('new')}>
          {t('wizard.workflow.createNew')}
        </button>
      </div>

      {mode === 'existing' ? (
        <div className="wizard-field">
          <label>{t('wizard.workflow.select')} <span className="required">*</span></label>
          <select
            value={data.workflowId || ''}
            onChange={e => {
              const w = workflows.find(x => String(x.workflow_id) === e.target.value);
              onChange({ ...data, workflowId: w ? w.workflow_id : null, workflowName: w ? w.workflow_name : '', workflowIsNew: false });
            }}
          >
            <option value="">{t('wizard.selectOption')}</option>
            {workflows.map(w => (
              <option key={w.workflow_id} value={w.workflow_id}>{w.workflow_name}</option>
            ))}
          </select>
        </div>
      ) : (
        <>
          <div className="wizard-field">
            <label>{t('wizard.workflow.name')} <span className="required">*</span></label>
            <input
              type="text"
              value={data.workflow_name || ''}
              onChange={e => updateField('workflow_name', e.target.value)}
              placeholder={t('wizard.workflow.namePlaceholder')}
            />
          </div>
          <div className="wizard-field">
            <label>{t('common:ui.description')}</label>
            <textarea
              value={data.workflow_description || ''}
              onChange={e => updateField('workflow_description', e.target.value)}
              rows={2}
              placeholder={t('wizard.workflow.descriptionPlaceholder')}
            />
          </div>

          <div className="wizard-tasks-header">
            <span>{t('wizard.workflow.tasks')}</span>
            <button className="wizard-add-row-btn" onClick={addTask}>{t('wizard.workflow.addTask')}</button>
          </div>
          <table className="wizard-tasks-table">
            <thead>
              <tr>
                <th style={{ width: '38%' }}>{t('wizard.workflow.taskName')}</th>
                <th style={{ width: '32%' }}>{t('common:ui.type')}</th>
                <th style={{ width: '20%' }}>{t('wizard.workflow.duration')}</th>
                <th style={{ width: '10%' }}></th>
              </tr>
            </thead>
            <tbody>
              {tasks.length === 0 ? (
                <tr><td colSpan={4} className="wizard-no-tasks">{t('wizard.workflow.noTasks')}</td></tr>
              ) : tasks.map((task, idx) => (
                <tr key={idx}>
                  <td>
                    <input
                      type="text"
                      value={task.task_name}
                      onChange={e => updateTask(idx, 'task_name', e.target.value)}
                      placeholder={t('wizard.workflow.taskNamePlaceholder')}
                    />
                  </td>
                  <td>
                    <select
                      value={task.task_type}
                      onChange={e => updateTask(idx, 'task_type', e.target.value)}
                    >
                      <option value="">{t('wizard.workflow.typeOption')}</option>
                      {taskTypes.map(tt => (
                        <option key={tt.type} value={tt.type}>{tt.name}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="number"
                      min="1"
                      value={task.estimated_duration_minutes}
                      onChange={e => updateTask(idx, 'estimated_duration_minutes', parseInt(e.target.value) || 60)}
                    />
                  </td>
                  <td>
                    <button className="wizard-delete-row" onClick={() => removeTask(idx)}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

// ── Step 1: Product Type ─────────────────────────────────────────────────────
function StepProductType({ data, onChange }) {
  const { t } = useTranslation('products');
  const [mode, setMode] = useState(data.productTypeId && !data.productTypeIsNew ? 'existing' : 'new');
  const [productTypes, setProductTypes] = useState([]);

  useEffect(() => {
    apiFetch(getApiUrl('/api/product-types/active')).then(r => r.json()).then(d => {
      setProductTypes(Array.isArray(d) ? d : []);
    }).catch(() => {});
  }, []);

  const switchMode = (m) => {
    setMode(m);
    if (m === 'existing') {
      onChange({ ...data, productTypeIsNew: false, type_name: '' });
    } else {
      onChange({ ...data, productTypeIsNew: true, productTypeId: null, productTypeName: '' });
    }
  };

  return (
    <div>
      {data.workflowName && (
        <div className="wizard-context">
          <span><strong>{t('wizard.workflow.label')}:</strong> {data.workflowName}</span>
        </div>
      )}

      <div className="wizard-toggle">
        <button className={mode === 'existing' ? 'active' : ''} onClick={() => switchMode('existing')}>
          {t('wizard.productType.useExisting')}
        </button>
        <button className={mode === 'new' ? 'active' : ''} onClick={() => switchMode('new')}>
          {t('wizard.productType.createNew')}
        </button>
      </div>

      {mode === 'existing' ? (
        <div className="wizard-field">
          <label>{t('wizard.productType.select')} <span className="required">*</span></label>
          <select
            value={data.productTypeId || ''}
            onChange={e => {
              const pt = productTypes.find(x => String(x.product_type_id) === e.target.value);
              onChange({ ...data, productTypeId: pt ? pt.product_type_id : null, productTypeName: pt ? pt.type_name : '', productTypeIsNew: false });
            }}
          >
            <option value="">{t('wizard.selectOption')}</option>
            {productTypes.map(pt => (
              <option key={pt.product_type_id} value={pt.product_type_id}>{pt.type_name}</option>
            ))}
          </select>
        </div>
      ) : (
        <>
          <div className="wizard-field">
            <label>{t('wizard.productType.name')} <span className="required">*</span></label>
            <input
              type="text"
              value={data.type_name || ''}
              onChange={e => onChange({ ...data, type_name: e.target.value })}
              placeholder={t('wizard.productType.namePlaceholder')}
            />
          </div>
          <div className="wizard-field">
            <label>{t('common:ui.description')}</label>
            <textarea
              value={data.type_description || ''}
              onChange={e => onChange({ ...data, type_description: e.target.value })}
              rows={2}
            />
          </div>
          <div className="wizard-field">
            <label>{t('wizard.productType.defaultWorkflow')}</label>
            <input type="text" readOnly value={data.workflowName || t('wizard.noneSelected')} />
          </div>
        </>
      )}
    </div>
  );
}

// ── Step 2: Product ──────────────────────────────────────────────────────────
function StepProduct({ data, onChange }) {
  const { t } = useTranslation('products');
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    fetchAllCustomers().then(setCustomers).catch(() => {}); // all of them, not just the first page (#310)
  }, []);

  const f = (field, value) => onChange({ ...data, [field]: value });

  return (
    <div>
      {(data.productTypeName) && (
        <div className="wizard-context">
          <span><strong>{t('wizard.productType.label')}:</strong> {data.productTypeName}</span>
        </div>
      )}

      <div className="wizard-grid-2">
        <div className="wizard-field">
          <label>{t('wizard.product.code')} <span className="required">*</span></label>
          <input
            type="text"
            value={data.product_code || ''}
            onChange={e => f('product_code', e.target.value)}
            placeholder={t('wizard.product.codePlaceholder')}
          />
        </div>
        <div className="wizard-field">
          <label>{t('wizard.product.uom')}</label>
          <select value={data.unit_of_measure || 'PCS'} onChange={e => f('unit_of_measure', e.target.value)}>
            {UOM_CODES.map(code => (
              <option key={code} value={code}>{`${code} — ${t(`uom.${code}`)}`}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="wizard-field">
        <label>{t('wizard.product.name')} <span className="required">*</span></label>
        <input
          type="text"
          value={data.product_name || ''}
          onChange={e => f('product_name', e.target.value)}
          placeholder={t('wizard.product.namePlaceholder')}
        />
      </div>

      <div className="wizard-grid-2">
        <div className="wizard-field">
          <label>{t('common:ui.customer')}</label>
          <select value={data.customer_id || ''} onChange={e => f('customer_id', e.target.value || null)}>
            <option value="">{t('wizard.product.noCustomer')}</option>
            {customers.map(c => (
              <option key={c.customer_id} value={c.customer_id}>{c.customer_name}</option>
            ))}
          </select>
        </div>
        <div className="wizard-field">
          <label>{t('wizard.product.standardPrice')}</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={data.standard_price || ''}
            onChange={e => f('standard_price', e.target.value)}
            placeholder="0.00"
          />
        </div>
      </div>

      <div className="wizard-field">
        <label>{t('common:ui.description')}</label>
        <textarea
          value={data.product_description || ''}
          onChange={e => f('product_description', e.target.value)}
          rows={2}
          placeholder={t('wizard.product.descriptionPlaceholder')}
        />
      </div>

      <div className="wizard-field">
        <label>{t('wizard.product.productType')}</label>
        <input type="text" readOnly value={data.productTypeName || t('wizard.noneSelected')} />
      </div>
    </div>
  );
}

// ── Step 3: BOM ──────────────────────────────────────────────────────────────
function StepBOM({ data, onChange }) {
  const { t } = useTranslation('products');
  const [mode, setMode] = useState(data.bomSkipped ? 'skip' : 'create');
  const [allItems, setAllItems] = useState([]);
  const [materials, setMaterials] = useState(data.bom_materials || []);

  useEffect(() => {
    apiFetch(getApiUrl('/api/boms/items?page_size=500')).then(r => r.json()).then(d => {
      setAllItems(d.items || d || []);
    }).catch(() => {});
  }, []);

  const switchMode = (m) => {
    setMode(m);
    onChange({ ...data, bomSkipped: m === 'skip' });
  };

  const f = (field, value) => onChange({ ...data, [field]: value });

  const addMaterial = () => {
    const next = [...materials, { item: null, quantity_per_unit: 1, unit_of_measure: 'PCS', waste_percentage: 0 }];
    setMaterials(next);
    onChange({ ...data, bom_materials: next });
  };

  const updateMaterial = (idx, field, value) => {
    const next = materials.map((m, i) => i === idx ? { ...m, [field]: value } : m);
    setMaterials(next);
    onChange({ ...data, bom_materials: next });
  };

  const removeMaterial = (idx) => {
    const next = materials.filter((_, i) => i !== idx);
    setMaterials(next);
    onChange({ ...data, bom_materials: next });
  };

  const totalCost = materials.reduce((sum, m) => {
    const cost = itemUnitCost(m.item);
    const qty = parseFloat(m.quantity_per_unit) || 0;
    const waste = parseFloat(m.waste_percentage) || 0;
    return sum + cost * qty * (1 + waste / 100);
  }, 0);

  return (
    <div>
      {data.productName && (
        <div className="wizard-context">
          <span><strong>{t('common:ui.product')}:</strong> {data.productCode} — {data.productName}</span>
        </div>
      )}

      <div className="wizard-toggle">
        <button className={mode === 'create' ? 'active' : ''} onClick={() => switchMode('create')}>
          {t('wizard.bom.createNow')}
        </button>
        <button className={mode === 'skip' ? 'active' : ''} onClick={() => switchMode('skip')}>
          {t('wizard.nav.skip')}
        </button>
      </div>

      {mode === 'skip' ? (
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', padding: '0.5rem 0' }}>
          {t('wizard.bom.skipHint')}
        </p>
      ) : (
        <>
          <div className="wizard-grid-2">
            <div className="wizard-field">
              <label>{t('wizard.bom.name')} <span className="required">*</span></label>
              <input
                type="text"
                value={data.bom_name || ''}
                onChange={e => f('bom_name', e.target.value)}
                placeholder={t('wizard.bom.namePlaceholder')}
              />
            </div>
            <div className="wizard-field">
              <label>{t('wizard.bom.version')}</label>
              <input
                type="text"
                value={data.bom_version || 'v1.0'}
                onChange={e => f('bom_version', e.target.value)}
              />
            </div>
          </div>
          <div className="wizard-grid-2">
            <div className="wizard-field">
              <label>{t('common:ui.status')}</label>
              <select value={data.bom_status || 'DRAFT'} onChange={e => f('bom_status', e.target.value)}>
                <option value="DRAFT">{t('wizard.bom.statusDraft')}</option>
                <option value="ACTIVE">{t('wizard.bom.statusActive')}</option>
              </select>
            </div>
            <div className="wizard-field">
              <label>{t('wizard.bom.yield')}</label>
              <input
                type="number"
                min="1"
                max="100"
                value={data.bom_yield || 100}
                onChange={e => f('bom_yield', e.target.value)}
              />
            </div>
          </div>

          <div className="wizard-tasks-header">
            <span>{t('wizard.bom.materials')}</span>
            <button className="wizard-add-row-btn" onClick={addMaterial}>{t('wizard.bom.addMaterial')}</button>
          </div>
          <table className="wizard-materials-table">
            <thead>
              <tr>
                <th style={{ width: '38%' }}>{t('wizard.bom.item')}</th>
                <th style={{ width: '16%' }}>{t('wizard.bom.qtyPerUnit')}</th>
                <th style={{ width: '18%' }}>{t('wizard.bom.uom')}</th>
                <th style={{ width: '16%' }}>{t('wizard.bom.wastePct')}</th>
                <th style={{ width: '12%' }}></th>
              </tr>
            </thead>
            <tbody>
              {materials.length === 0 ? (
                <tr><td colSpan={5} className="wizard-no-tasks">{t('wizard.bom.noMaterials')}</td></tr>
              ) : materials.map((mat, idx) => (
                <tr key={idx}>
                  <td>
                    <ItemAutocomplete
                      value={mat.item}
                      allItems={allItems}
                      onSelect={item => {
                        // Merge item + unit_of_measure in one operation so both fields
                        // are written from the same materials snapshot (avoids stale
                        // closure overwrite when updateMaterial is called twice).
                        const patch = item
                          ? { item, unit_of_measure: item.unit_of_measure || mat.unit_of_measure || 'PCS' }
                          : { item: null };
                        const next = materials.map((m, i) => i === idx ? { ...m, ...patch } : m);
                        setMaterials(next);
                        onChange({ ...data, bom_materials: next });
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      value={mat.quantity_per_unit}
                      onChange={e => updateMaterial(idx, 'quantity_per_unit', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={mat.unit_of_measure}
                      onChange={e => updateMaterial(idx, 'unit_of_measure', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={mat.waste_percentage}
                      onChange={e => updateMaterial(idx, 'waste_percentage', e.target.value)}
                    />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button className="wizard-delete-row" onClick={() => removeMaterial(idx)}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {materials.length > 0 && (
            <div className="wizard-cost-summary">
              {t('wizard.bom.estimatedCost')} <strong>${totalCost.toFixed(4)}</strong>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Step 4: Done ─────────────────────────────────────────────────────────────
function StepDone({ data, onViewProduct, onClose }) {
  const { t } = useTranslation('products');
  return (
    <div>
      <div className="wizard-done-icon">✅</div>
      <div className="wizard-done-title">{t('wizard.done.title')}</div>

      <div className="wizard-summary-card">
        <div className="wizard-summary-row">
          <span className="wizard-summary-label">{t('wizard.workflow.label')}</span>
          <span className="wizard-summary-value">{data.workflowName || '—'}</span>
        </div>
        <div className="wizard-summary-row">
          <span className="wizard-summary-label">{t('wizard.productType.label')}</span>
          <span className="wizard-summary-value">{data.productTypeName || '—'}</span>
        </div>
        <div className="wizard-summary-row">
          <span className="wizard-summary-label">{t('common:ui.product')}</span>
          <span className="wizard-summary-value">{data.productCode} — {data.productName}</span>
        </div>
        <div className="wizard-summary-row">
          <span className="wizard-summary-label">{t('wizard.steps.bom')}</span>
          <span className="wizard-summary-value">
            {data.bomSkipped || !data.bomName
              ? t('wizard.done.noBom')
              : data.bomName}
          </span>
        </div>
      </div>

      <div className="wizard-done-actions">
        <button
          className="btn-primary"
          onClick={() => {
            window.location.href = `/sales?newQuote=true&productId=${data.productId}`;
          }}
        >
          {t('wizard.done.createQuotation')}
        </button>
        <button className="btn-secondary" onClick={onViewProduct}>
          {t('wizard.done.viewProduct')}
        </button>
        <button className="btn-secondary" onClick={onClose}>
          {t('common:ui.close')}
        </button>
      </div>
    </div>
  );
}

// ── Main Wizard ──────────────────────────────────────────────────────────────
export default function NewProductWizard({ open, onClose, onProductCreated }) {
  const { t } = useTranslation('products');
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [wizardData, setWizardData] = useState({ ...DEFAULT_WIZARD_DATA });
  const [draft, setDraft] = useState(null); // non-null = resume prompt showing

  // ── Draft persistence ─────────────────────────────────────────────────────

  const persistDraft = useCallback((data, step) => {
    try {
      localStorage.setItem(WIZARD_DRAFT_KEY, JSON.stringify({
        data,
        step,
        savedAt: new Date().toISOString(),
      }));
    } catch {}
  }, []);

  const clearDraft = useCallback(() => {
    try { localStorage.removeItem(WIZARD_DRAFT_KEY); } catch {}
  }, []);

  // Check for a saved draft whenever the wizard is opened
  useEffect(() => {
    if (!open) return;
    try {
      const raw = localStorage.getItem(WIZARD_DRAFT_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        // Only offer resume if at least the workflow was saved
        if (saved?.data?.workflowId) {
          setDraft(saved);
          return;
        }
      }
    } catch {}
    setDraft(null);
  }, [open]);

  const handleResume = useCallback(() => {
    if (!draft) return;
    setWizardData(draft.data);
    setCurrentStep(draft.step);
    setDraft(null);
  }, [draft]);

  const handleStartFresh = useCallback(() => {
    clearDraft();
    setDraft(null);
    setCurrentStep(0);
    setError('');
    setWizardData({ ...DEFAULT_WIZARD_DATA });
  }, [clearDraft]);

  // ── Reset / close ─────────────────────────────────────────────────────────

  const resetWizard = useCallback(() => {
    setCurrentStep(0);
    setError('');
    setSaving(false);
    setWizardData({ ...DEFAULT_WIZARD_DATA });
    setDraft(null);
  }, []);

  const handleClose = useCallback(() => {
    // Don't clear in-memory state or draft — user can resume later
    onClose();
  }, [onClose]);

  // ── Save helpers ─────────────────────────────────────────────────────────

  const saveWorkflow = async () => {
    if (wizardData.workflowIsNew) {
      // Already created this session — skip re-creation (back+forward scenario)
      if (wizardData.workflowId) {
        return { workflowId: wizardData.workflowId, workflowName: wizardData.workflowName };
      }
      if (!wizardData.workflow_name?.trim()) throw new Error(t('wizard.errors.workflowNameRequired'));
      const res = await apiFetch(getApiUrl('/api/workflows/'), {
        method: 'POST',
        body: JSON.stringify({
          workflow_name: wizardData.workflow_name.trim(),
          workflow_description: wizardData.workflow_description || '',
          is_active: true,
        }),
      });
      if (!res.ok) throw new Error(t('wizard.errors.createWorkflowFailed', { status: res.status }));
      const wf = await res.json();
      const wfId = wf.workflow_id;
      const wfName = wf.workflow_name;

      // Save tasks
      const tasks = wizardData.tasks || [];
      for (let i = 0; i < tasks.length; i++) {
        const task = tasks[i];
        if (!task.task_name?.trim()) continue;
        const tr = await apiFetch(getApiUrl(`/api/workflows/${wfId}/tasks`), {
          method: 'POST',
          body: JSON.stringify({
            workflow_id: wfId,
            task_name: task.task_name.trim(),
            task_description: '',
            task_sequence: i + 1,
            task_type: task.task_type || 'GENERAL',
            estimated_duration_minutes: parseInt(task.estimated_duration_minutes) || 60,
            is_required: true,
            is_active: true,
          }),
        });
        if (!tr.ok) throw new Error(t('wizard.errors.createTaskFailed', { name: task.task_name, status: tr.status }));
      }
      return { workflowId: wfId, workflowName: wfName };
    }
    // existing
    if (!wizardData.workflowId) throw new Error(t('wizard.errors.selectWorkflow'));
    return { workflowId: wizardData.workflowId, workflowName: wizardData.workflowName };
  };

  const saveProductType = async (workflowId) => {
    if (wizardData.productTypeIsNew) {
      // Already created this session — skip re-creation (back+forward scenario)
      if (wizardData.productTypeId) {
        return { productTypeId: wizardData.productTypeId, productTypeName: wizardData.productTypeName };
      }
      if (!wizardData.type_name?.trim()) throw new Error(t('wizard.errors.typeNameRequired'));
      const res = await apiFetch(getApiUrl('/api/product-types/'), {
        method: 'POST',
        body: JSON.stringify({
          type_name: wizardData.type_name.trim(),
          description: wizardData.type_description || '',
          default_workflow_id: workflowId,
          is_active: true,
        }),
      });
      if (!res.ok) throw new Error(t('wizard.errors.createTypeFailed', { status: res.status }));
      const pt = await res.json();
      return { productTypeId: pt.product_type_id, productTypeName: pt.type_name };
    }
    if (!wizardData.productTypeId) throw new Error(t('wizard.errors.selectType'));
    return { productTypeId: wizardData.productTypeId, productTypeName: wizardData.productTypeName };
  };

  const saveProduct = async (productTypeId) => {
    // Already created this session — skip re-creation (back+forward scenario)
    if (wizardData.productId) {
      return { productId: wizardData.productId, productName: wizardData.productName, productCode: wizardData.productCode };
    }
    if (!wizardData.product_code?.trim()) throw new Error(t('wizard.errors.productCodeRequired'));
    if (!wizardData.product_name?.trim()) throw new Error(t('wizard.errors.productNameRequired'));
    if (!productTypeId) throw new Error(t('wizard.errors.productTypeRequired'));
    const body = {
      product_code: wizardData.product_code.trim(),
      product_name: wizardData.product_name.trim(),
      product_type_id: productTypeId,
      unit_of_measure: wizardData.unit_of_measure || 'PCS',
      is_active: true,
    };
    if (wizardData.customer_id) body.customer_id = parseInt(wizardData.customer_id);
    if (wizardData.standard_price) body.standard_price = parseFloat(wizardData.standard_price);
    if (wizardData.product_description) body.description = wizardData.product_description;

    const res = await apiFetch(getApiUrl('/api/products'), {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.message || errData.detail || t('wizard.errors.createProductFailed', { status: res.status }));
    }
    const prod = await res.json();
    return { productId: prod.product_id, productName: wizardData.product_name.trim(), productCode: wizardData.product_code.trim() };
  };

  const saveBOM = async (productId) => {
    if (wizardData.bomSkipped) return { bomId: null, bomName: '', bomSkipped: true };
    // Already created this session — skip re-creation (back+forward scenario)
    if (wizardData.bomId) {
      return { bomId: wizardData.bomId, bomName: wizardData.bomName, bomSkipped: false };
    }
    if (!wizardData.bom_name?.trim()) throw new Error(t('wizard.errors.bomNameRequired'));

    // Validate all material rows have an item properly selected from the dropdown
    const bomMaterials = wizardData.bom_materials || [];
    if (bomMaterials.length > 0) {
      const unlinked = bomMaterials.filter(m => !m.item?.item_id);
      if (unlinked.length > 0) {
        throw new Error(t('wizard.errors.unlinkedMaterials', { count: unlinked.length }));
      }
    }

    const res = await apiFetch(getApiUrl('/api/boms'), {
      method: 'POST',
      body: JSON.stringify({
        product_id: productId,
        bom_name: wizardData.bom_name.trim(),
        bom_version: wizardData.bom_version || 'v1.0',
        status: wizardData.bom_status || 'DRAFT',
        is_default: true,
        yield_percentage: parseFloat(wizardData.bom_yield || 100),
      }),
    });
    if (!res.ok) throw new Error(t('wizard.errors.createBomFailed', { status: res.status }));
    const bom = await res.json();
    const bomId = bom.bom_id;

    // Save materials
    for (const mat of bomMaterials) {
      if (!mat.item?.item_id) continue;
      const mr = await apiFetch(getApiUrl(`/api/boms/${bomId}/items`), {
        method: 'POST',
        body: JSON.stringify({
          item_id: mat.item.item_id,
          quantity_per_unit: parseFloat(parseFloat(mat.quantity_per_unit || 1).toFixed(6)),
          unit_of_measure: mat.unit_of_measure || mat.item.unit_of_measure || 'PCS',
          unit_cost: parseFloat(itemUnitCost(mat.item).toFixed(4)),
          waste_percentage: parseFloat(mat.waste_percentage || 0),
          sequence_order: 1,
          is_optional: false,
        }),
      });
      if (!mr.ok) throw new Error(t('wizard.errors.addMaterialFailed', { name: mat.item.item_name, status: mr.status }));
    }
    return { bomId, bomName: bom.bom_name, bomSkipped: false };
  };

  // ── Step navigation ──────────────────────────────────────────────────────

  const handleNext = async () => {
    setError('');
    setSaving(true);

    try {
      if (currentStep === 0) {
        const result = await saveWorkflow();
        const updated = { ...wizardData, ...result };
        setWizardData(updated);
        persistDraft(updated, 1);
        setCurrentStep(1);

      } else if (currentStep === 1) {
        const result = await saveProductType(wizardData.workflowId);
        const updated = { ...wizardData, ...result };
        setWizardData(updated);
        persistDraft(updated, 2);
        setCurrentStep(2);

      } else if (currentStep === 2) {
        const result = await saveProduct(wizardData.productTypeId);
        const updated = { ...wizardData, ...result };
        setWizardData(updated);
        persistDraft(updated, 3);
        setCurrentStep(3);

      } else if (currentStep === 3) {
        const result = await saveBOM(wizardData.productId);
        const updated = { ...wizardData, ...result };
        setWizardData(updated);
        clearDraft(); // wizard complete — draft no longer needed
        if (onProductCreated) onProductCreated();
        setCurrentStep(4);
      }
    } catch (err) {
      setError(err.message || t('wizard.errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    setError('');
    setCurrentStep(s => Math.max(0, s - 1));
  };

  const handleViewProduct = () => {
    onClose();
    // Signal ProductsPage to open this product
    if (wizardData.productId) {
      window._wizardProductId = wizardData.productId;
      window.dispatchEvent(new CustomEvent('wizard:viewProduct', { detail: { productId: wizardData.productId } }));
    }
  };

  if (!open) return null;

  const stepLabels = STEPS.map(step => t(`wizard.steps.${step}`));

  return (
    <div className="wizard-overlay" onClick={e => { if (e.target === e.currentTarget) handleClose(); }}>
      <div className="wizard-modal">
        {/* Header */}
        <div className="wizard-header">
          <div className="wizard-header-top">
            <h2>{t('wizard.title', 'New Product Setup')}</h2>
            <button className="wizard-close" onClick={handleClose} aria-label={t('common:ui.close')}>✕</button>
          </div>

          {/* Progress */}
          <div className="wizard-steps">
            {STEPS.map((step, idx) => (
              <React.Fragment key={step}>
                {idx > 0 && (
                  <div className={`wizard-connector${idx <= currentStep ? ' completed' : ''}`} />
                )}
                <div className={`wizard-step${currentStep === idx ? ' active' : ''}${currentStep > idx ? ' completed' : ''}`}>
                  <div className="wizard-step-circle">
                    {currentStep > idx ? '✓' : idx + 1}
                  </div>
                  <div className="wizard-step-label">{stepLabels[idx]}</div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="wizard-body">
          {/* Resume prompt — shown when a previous draft is detected */}
          {draft && (
            <div className="wizard-resume-prompt">
              <div className="wizard-resume-icon">📋</div>
              <div className="wizard-resume-text">
                <strong>{t('wizard.resume.title')}</strong>
                <span>
                  {draft.data?.productCode
                    ? t('wizard.resume.product', { code: draft.data.productCode, name: draft.data.productName })
                    : draft.data?.workflowName
                    ? t('wizard.resume.workflow', { name: draft.data.workflowName })
                    : t('wizard.resume.partial')}
                  {' · '}
                  {draft.savedAt
                    ? t('wizard.resume.lastSaved', { date: formatAppDate(draft.savedAt, 'datetime') })
                    : ''}
                </span>
              </div>
              <div className="wizard-resume-actions">
                <button className="wizard-btn-next" onClick={handleResume}>{t('wizard.resume.resume')}</button>
                <button className="wizard-btn-back" onClick={handleStartFresh}>{t('wizard.resume.startFresh')}</button>
              </div>
            </div>
          )}

          {!draft && error && <div className="wizard-error">{error}</div>}

          {!draft && currentStep === 0 && (
            <StepWorkflow data={wizardData} onChange={setWizardData} />
          )}
          {!draft && currentStep === 1 && (
            <StepProductType data={wizardData} onChange={setWizardData} />
          )}
          {!draft && currentStep === 2 && (
            <StepProduct data={wizardData} onChange={setWizardData} />
          )}
          {!draft && currentStep === 3 && (
            <StepBOM data={wizardData} onChange={setWizardData} />
          )}
          {!draft && currentStep === 4 && (
            <StepDone data={wizardData} onViewProduct={handleViewProduct} onClose={handleClose} />
          )}
        </div>

        {/* Footer (hidden on Done step and during resume prompt) */}
        {!draft && currentStep < 4 && (
          <div className="wizard-footer">
            <div className="wizard-footer-left">
              {currentStep > 0 && (
                <button className="wizard-btn-back" onClick={handleBack} disabled={saving}>
                  ← {t('wizard.nav.back')}
                </button>
              )}
            </div>
            <div className="wizard-footer-right">
              {currentStep === 3 && !wizardData.bomSkipped && (
                <button
                  className="wizard-btn-skip"
                  onClick={() => {
                    setWizardData(prev => ({ ...prev, bomId: null, bomName: '', bomSkipped: true }));
                    clearDraft();
                    if (onProductCreated) onProductCreated();
                    setCurrentStep(4);
                  }}
                  disabled={saving}
                >
                  {t('wizard.nav.skip')}
                </button>
              )}
              <button className="wizard-btn-next" onClick={handleNext} disabled={saving}>
                {saving ? t('wizard.nav.saving') : (currentStep === 3 ? t('wizard.nav.finish') : `${t('wizard.nav.next')} →`)}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
